import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import {
  api, PresetMeta, PresetFacilitiesResponse, PresetFacilityItem, PresetAreaItem,
} from "../lib/api";

export function PresetsPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const { data: presets, isLoading } = useQuery({
    queryKey: ["presets"],
    queryFn: () => api.get<PresetMeta[]>("/api/v1/presets"),
  });

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження…</div>;

  if (selected) {
    return <PresetDetail code={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div style={{ padding: "24px 32px" }}>
      <h1 style={{ fontSize: 22, color: "#1a365d", margin: "0 0 6px" }}>
        Аналітичні пресети
      </h1>
      <p style={{ color: "#718096", fontSize: 13, marginBottom: 20 }}>
        7 системних фільтрів — швидкий доступ до типових сценаріїв аналізу.
        Клікніть пресет, щоб побачити лікарні, які йому відповідають.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
        {presets?.map((p) => (
          <button
            key={p.code}
            onClick={() => setSelected(p.code)}
            style={{
              textAlign: "left", padding: 16, background: "white",
              border: `2px solid ${p.color}30`, borderRadius: 8,
              cursor: "pointer", transition: "all 0.15s",
              display: "flex", flexDirection: "column", gap: 8,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = p.color;
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${p.color}30`;
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>{p.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: p.color }}>
                  {p.name_uk}
                </div>
                {p.is_placeholder && (
                  <div style={{ fontSize: 10, color: "#a0aec0", marginTop: 2 }}>
                    ⚠ ще не всі дані підключені
                  </div>
                )}
              </div>
              <ChevronRight size={16} color="#a0aec0" />
            </div>
            <div style={{ fontSize: 12.5, color: "#4a5568", lineHeight: 1.4 }}>
              {p.description}
            </div>
            <code style={{
              fontSize: 10.5, color: "#718096", background: "#f7fafc",
              padding: "4px 8px", borderRadius: 3, fontFamily: "ui-monospace, monospace",
            }}>
              {p.rationale}
            </code>
          </button>
        ))}
      </div>
    </div>
  );
}

function PresetDetail({ code, onBack }: { code: string; onBack: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["preset-facilities", code],
    queryFn: () => api.get<PresetFacilitiesResponse>(`/api/v1/presets/${code}/facilities?limit=500`),
  });

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження…</div>;
  if (!data) return null;

  const p = data.preset;

  return (
    <div style={{ padding: "24px 32px" }}>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: "#4299e1", cursor: "pointer",
        fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4, padding: 0,
      }}>
        <ArrowLeft size={14} /> До переліку пресетів
      </button>

      <div style={{ marginTop: 12, marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, color: p.color, margin: "0 0 6px" }}>
          {p.icon} {p.name_uk}
          <span style={{ fontSize: 14, color: "#718096", marginLeft: 12, fontWeight: 400 }}>
            знайдено: <strong>{data.total}</strong>
          </span>
        </h1>
        <p style={{ color: "#4a5568", fontSize: 13, marginBottom: 6, maxWidth: 800 }}>{p.description}</p>
        <code style={{
          fontSize: 11, color: "#718096", background: "#f7fafc",
          padding: "3px 8px", borderRadius: 3, display: "inline-block",
        }}>
          {p.rationale}
        </code>
      </div>

      {data.total === 0 && (
        <div style={{ padding: 40, background: "#f7fafc", borderRadius: 6, textAlign: "center", color: "#718096" }}>
          Жодна лікарня не відповідає критеріям пресету.
          {p.is_placeholder && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              (Очікувано: пресет потребує даних, які ще не підключено)
            </div>
          )}
        </div>
      )}

      {data.kind === "facility_level" && data.total > 0 && (
        <FacilityTable items={data.items as PresetFacilityItem[]} />
      )}

      {data.kind === "area_level" && data.total > 0 && (
        <AreaTable items={data.items as PresetAreaItem[]} />
      )}
    </div>
  );
}

function FacilityTable({ items }: { items: PresetFacilityItem[] }) {
  return (
    <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
      <thead>
        <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
          <th style={th}>Назва</th>
          <th style={th}>Область</th>
          <th style={th}>Тип</th>
          <th style={{ ...th, textAlign: "right" }}>Composite</th>
          <th style={{ ...th, textAlign: "right" }}>Activ.</th>
          <th style={{ ...th, textAlign: "right" }}>Effic.</th>
          <th style={{ ...th, textAlign: "right" }}>Dynam.</th>
        </tr>
      </thead>
      <tbody>
        {items.map((f) => (
          <tr key={f.edrpou} style={{ borderTop: "1px solid #e2e8f0" }}>
            <td style={td}>
              <Link to={`/facilities/${f.edrpou}`} style={{ color: "#2c5282", textDecoration: "none" }}>
                {f.name.length > 60 ? f.name.slice(0, 60) + "…" : f.name}
              </Link>
              <div style={{ fontSize: 10.5, color: "#a0aec0" }}>
                ЄДРПОУ {f.edrpou} {f.settlement_name ? `· ${f.settlement_name}` : ""}
              </div>
            </td>
            <td style={td}>{f.area || "—"}</td>
            <td style={td}>{f.care_type || "—"}</td>
            <td style={{ ...td, textAlign: "right", fontWeight: 600, color: scoreColor(f.score_composite) }}>
              {f.score_composite ?? "—"}
            </td>
            <td style={{ ...td, textAlign: "right", color: scoreColor(f.score_activity) }}>
              {f.score_activity ?? "—"}
            </td>
            <td style={{ ...td, textAlign: "right", color: scoreColor(f.score_efficiency) }}>
              {f.score_efficiency ?? "—"}
            </td>
            <td style={{ ...td, textAlign: "right", color: scoreColor(f.score_dynamics) }}>
              {f.score_dynamics ?? "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AreaTable({ items }: { items: PresetAreaItem[] }) {
  return (
    <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
      <thead>
        <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
          <th style={th}>Область</th>
          <th style={{ ...th, textAlign: "right" }}>Лікарень</th>
          <th style={{ ...th, textAlign: "right" }}>Сер. Composite</th>
        </tr>
      </thead>
      <tbody>
        {items.map((a) => (
          <tr key={a.area} style={{ borderTop: "1px solid #e2e8f0" }}>
            <td style={td}>{a.area}</td>
            <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{a.facility_count}</td>
            <td style={{ ...td, textAlign: "right", color: scoreColor(a.avg_composite) }}>
              {a.avg_composite ?? "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function scoreColor(v: number | null | undefined): string {
  if (v === null || v === undefined) return "#a0aec0";
  if (v >= 75) return "#38a169";
  if (v >= 50) return "#d69e2e";
  if (v >= 25) return "#dd6b20";
  return "#e53e3e";
}

const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 600, fontSize: 12 };
const td: React.CSSProperties = { padding: "8px 12px" };
